/**
 * Read/write the Corpus **scope** on a chat thread.
 *
 * The scope is the set of Corpus buckets + folders + docs whose chunks the
 * semantic search may consider on turns for this thread. It replaces the
 * old per-thread "pinned docs" model (see mem://features/corpus-rag.md).
 * Every scope kind unifies under a single "reference" concept — the
 * picker lets users include buckets, folders, and docs in any combination.
 * Empty scope = search the whole workspace (today's default).
 *
 * Planning + Writing agents inherit the union of scopes across every
 * chat thread on a Piece via `listPieceInheritedScope`.
 */
import { supabase } from "@/integrations/supabase/client";
import type { CorpusBucket } from "@/lib/workspaceCorpus";

// Soft ceilings — the retrieval-time chunk budget is the real cap; these
// just prevent runaway UI selections.
export const MAX_SCOPED_REFS = 100;

export type ThreadScope = {
  documentIds: string[];
  folderIds: string[];
  buckets: CorpusBucket[];
};

const EMPTY_SCOPE: ThreadScope = { documentIds: [], folderIds: [], buckets: [] };

type ThreadScopeRow = {
  scoped_document_ids: string[] | null;
  scoped_folder_ids: string[] | null;
  scoped_buckets: string[] | null;
};

const VALID_BUCKETS: readonly CorpusBucket[] = ["primary", "secondary", "supplemental"];

function normalizeBuckets(input: string[] | null | undefined): CorpusBucket[] {
  const seen = new Set<CorpusBucket>();
  for (const b of input ?? []) {
    if ((VALID_BUCKETS as readonly string[]).includes(b)) seen.add(b as CorpusBucket);
  }
  return Array.from(seen);
}

export async function getThreadScope(threadId: string): Promise<ThreadScope> {
  const { data, error } = await supabase
    .from("composer_chat_threads")
    .select("scoped_document_ids, scoped_folder_ids, scoped_buckets")
    .eq("id", threadId)
    .maybeSingle();
  if (error) throw error;
  if (!data) return EMPTY_SCOPE;
  const row = data as unknown as ThreadScopeRow;
  return {
    documentIds: row.scoped_document_ids ?? [],
    folderIds: row.scoped_folder_ids ?? [],
    buckets: normalizeBuckets(row.scoped_buckets),
  };
}

export async function setThreadScope(
  threadId: string,
  scope: ThreadScope,
): Promise<void> {
  const documentIds = Array.from(new Set(scope.documentIds)).slice(0, MAX_SCOPED_REFS);
  const folderIds = Array.from(new Set(scope.folderIds)).slice(0, MAX_SCOPED_REFS);
  const buckets = normalizeBuckets(scope.buckets);
  const { error } = await supabase
    .from("composer_chat_threads")
    .update({
      scoped_document_ids: documentIds,
      scoped_folder_ids: folderIds,
      scoped_buckets: buckets,
    } as never)
    .eq("id", threadId);
  if (error) throw error;
}

/**
 * Union of scopes across every chat thread on the Piece. Planner and
 * Writer edge functions do the equivalent server-side; this helper feeds
 * the read-only "Scoped from AI Chat" chip row in the Planning rail.
 */
export async function listPieceInheritedScope(
  workspaceId: string,
  pieceId: string,
): Promise<ThreadScope> {
  const { data, error } = await supabase
    .from("composer_chat_threads")
    .select("scoped_document_ids, scoped_folder_ids, scoped_buckets")
    .eq("workspace_id", workspaceId)
    .eq("piece_id", pieceId);
  if (error) throw error;
  const rows = (data ?? []) as unknown as ThreadScopeRow[];
  const docs = new Set<string>();
  const folders = new Set<string>();
  const buckets = new Set<CorpusBucket>();
  for (const r of rows) {
    for (const id of r.scoped_document_ids ?? []) docs.add(id);
    for (const id of r.scoped_folder_ids ?? []) folders.add(id);
    for (const b of normalizeBuckets(r.scoped_buckets)) buckets.add(b);
  }
  return {
    documentIds: Array.from(docs),
    folderIds: Array.from(folders),
    buckets: Array.from(buckets),
  };
}
